import { useState } from 'react'
import {
  Hash,
  Calendar,
  ThumbsUp,
  MessageSquare,
  Eye,
  CheckCircle,
  Edit3,
  RefreshCw,
  Trash2,
  Clock,
  Send,
  AlertTriangle,
  MessageSquareReply,
} from 'lucide-react'
import { BetaBadge } from '@/components/beta-badge'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { Post, PostStatus } from './types'
import { STATUS_LABELS } from './types'
import { STATUS_BADGE } from '@/lib/status-styles'

// Post-status badge styling — all running on the obsidian dark theme so the
// pale Tailwind pastel-on-light pairings would be unreadable; use our shared
// alpha-fill variants instead.
const STATUS_STYLES: Record<PostStatus, string> = {
  draft: STATUS_BADGE.neutral,
  pending_review: STATUS_BADGE.warn,
  approved: STATUS_BADGE.info,
  published: STATUS_BADGE.success,
  scheduled: STATUS_BADGE.warn,
  failed: STATUS_BADGE.danger,
}

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/** `YYYY-MM-DDTHH:mm` in LOCAL time, for a datetime-local `min`. */
function localInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function formatNumber(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
  return String(n)
}

type PostCardProps = {
  readonly post: Post
  readonly onApprove?: (id: string) => void
  readonly onPublish?: (id: string) => void
  readonly onSchedule?: (id: string, scheduledAt: string) => void
  readonly onEdit?: (post: Post) => void
  readonly onRegenerate?: (post: Post) => void
  readonly onDelete?: (id: string) => void
  /** Pro: open the comment keyword DM editor. */
  readonly onDmTrigger?: (post: Post) => void
  readonly isApproving?: boolean
  readonly isPublishing?: boolean
  readonly isScheduling?: boolean
  readonly isDeleting?: boolean
}

export function PostCard({
  post,
  onApprove,
  onPublish,
  onSchedule,
  onEdit,
  onRegenerate,
  onDelete,
  onDmTrigger,
  isApproving = false,
  isPublishing = false,
  isScheduling = false,
  isDeleting = false,
}: PostCardProps) {
  const [showFull, setShowFull] = useState(false)
  const [showSchedulePicker, setShowSchedulePicker] = useState(false)
  const [scheduleDate, setScheduleDate] = useState('')

  const preview = post.content.slice(0, 200)
  const hasMore = post.content.length > 200
  const displayContent = showFull ? post.content : preview

  const hashtagCount = post.hashtags?.length ?? 0

  const dateLabel =
    post.status === 'published' && post.published_at
      ? `Published ${formatDate(post.published_at)}`
      : post.status === 'scheduled' && post.scheduled_for
        ? `Scheduled ${formatDate(post.scheduled_for)}`
        : `Created ${formatDate(post.created_at)}`

  const showApproveButton =
    (post.status === 'draft' || post.status === 'pending_review') && onApprove
  // 'failed' = retry; 'scheduled' = post it now instead of waiting.
  const showPublishButton = post.status !== 'published' && onPublish
  const showScheduleButton = post.status !== 'published' && onSchedule
  const showEditButton = post.status !== 'published' && onEdit
  const showRegenerateButton = (post.status === 'draft' || post.status === 'pending_review') && onRegenerate
  const showDeleteButton = post.status !== 'published' && onDelete
  const showDmButton = Boolean(onDmTrigger)

  // The API returns metrics_* columns; likes/comments/impressions are older aliases.
  const likes = post.metrics_likes ?? post.likes
  const comments = post.metrics_comments ?? post.comments
  const impressions = post.metrics_impressions ?? post.impressions

  return (
    <div className="rounded-lg border bg-card shadow-sm hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 p-4 pb-3">
        <Badge className={cn('text-xs border', STATUS_STYLES[post.status])}>
          {STATUS_LABELS[post.status]}
        </Badge>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {post.status === 'scheduled' ? (
            <Clock className="h-3 w-3" />
          ) : (
            <Calendar className="h-3 w-3" />
          )}
          <span>{dateLabel}</span>
        </div>
      </div>

      {/* Why the last publish failed (set by the scheduled publisher) */}
      {post.status === 'failed' && (
        <div className="mx-4 mb-3 flex items-start gap-2 rounded-md border border-red-500/25 bg-red-500/10 px-3 py-2">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-300" />
          <p className="text-xs text-red-300">
            {post.publish_error || 'This post could not be published.'} Use Publish Now to retry or Schedule to pick a new time.
          </p>
        </div>
      )}

      {/* Comment keyword DM trigger */}
      {post.dm_keyword && (
        <div className="mx-4 mb-3 flex items-center gap-2 rounded-md border border-primary/25 bg-primary/10 px-3 py-1.5 text-xs">
          <MessageSquareReply className="h-3.5 w-3.5 text-cobalt-bright" />
          <span>Comments with &quot;{post.dm_keyword}&quot; get your DM</span>
          <BetaBadge className="ml-auto" />
        </div>
      )}

      {/* Guardrail warnings */}
      {post.guardrail_warnings && post.guardrail_warnings.length > 0 && (
        <div className="mx-4 mb-3 rounded-md border border-amber-500/25 bg-amber-500/10 px-3 py-2">
          <p className="text-xs font-medium text-yellow-800 ">
            Review required:
          </p>
          {post.guardrail_warnings.map((warning) => (
            <p key={warning} className="mt-0.5 text-xs text-amber-300 ">
              {warning}
            </p>
          ))}
        </div>
      )}

      {/* Content preview */}
      <div className="px-4 pb-3">
        <p className="whitespace-pre-line text-sm leading-relaxed text-foreground">
          {displayContent}
          {!showFull && hasMore && '...'}
        </p>
        {hasMore && (
          <button
            type="button"
            onClick={() => setShowFull((v) => !v)}
            className="mt-1 text-xs text-cobalt-bright hover:underline focus:outline-none"
          >
            {showFull ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>

      {/* Metadata row */}
      <div className="flex items-center gap-3 border-t px-4 py-2.5 text-xs text-muted-foreground">
        {hashtagCount > 0 && (
          <div className="flex items-center gap-1">
            <Hash className="h-3 w-3" />
            <span>{hashtagCount} hashtags</span>
          </div>
        )}
        {post.topic && (
          <span className="truncate italic">{post.topic}</span>
        )}
      </div>

      {/* Performance metrics (published only) */}
      {post.status === 'published' &&
        (likes !== undefined || comments !== undefined || impressions !== undefined) && (
          <div className="flex items-center gap-4 border-t px-4 py-2.5 text-xs text-muted-foreground">
            {likes !== undefined && (
              <div className="flex items-center gap-1">
                <ThumbsUp className="h-3 w-3" />
                <span>{formatNumber(likes)}</span>
              </div>
            )}
            {comments !== undefined && (
              <div className="flex items-center gap-1">
                <MessageSquare className="h-3 w-3" />
                <span>{formatNumber(comments)}</span>
              </div>
            )}
            {impressions !== undefined && impressions > 0 && (
              <div className="flex items-center gap-1">
                <Eye className="h-3 w-3" />
                <span>{formatNumber(impressions)} impressions</span>
              </div>
            )}
          </div>
        )}

      {/* Schedule picker (inline) */}
      {showSchedulePicker && (
        <div className="flex items-center gap-2 border-t px-4 py-2.5">
          <input
            type="datetime-local"
            value={scheduleDate}
            onChange={(e) => setScheduleDate(e.target.value)}
            // Local time: toISOString() is UTC, which set the minimum hours off
            // for anyone outside UTC (4-5 h ahead in New York).
            min={localInputValue(new Date(Date.now() + 5 * 60_000))}
            className="h-7 rounded-md border bg-background px-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring flex-1"
          />
          <Button
            size="sm"
            className="h-7 text-xs"
            disabled={!scheduleDate || isScheduling}
            onClick={() => {
              if (scheduleDate && onSchedule) {
                onSchedule(post.id, new Date(scheduleDate).toISOString())
                setShowSchedulePicker(false)
                setScheduleDate('')
              }
            }}
          >
            {isScheduling ? 'Scheduling...' : 'Confirm'}
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowSchedulePicker(false)}>
            Cancel
          </Button>
        </div>
      )}

      {/* Action buttons */}
      {(showApproveButton || showPublishButton || showScheduleButton || showEditButton || showRegenerateButton || showDeleteButton || showDmButton) && (
        <div className="flex flex-wrap items-center gap-2 border-t px-4 py-3">
          {showPublishButton && (
            <Button
              size="sm"
              onClick={() => onPublish(post.id)}
              disabled={isPublishing}
              className="h-7 gap-1.5 text-xs"
            >
              <Send className="h-3.5 w-3.5" />
              {isPublishing ? 'Publishing...' : 'Publish Now'}
            </Button>
          )}
          {showScheduleButton && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowSchedulePicker(true)}
              disabled={isScheduling}
              className="h-7 gap-1.5 text-xs"
            >
              <Clock className="h-3.5 w-3.5" />
              {post.status === 'scheduled' ? 'Reschedule' : 'Schedule'}
            </Button>
          )}
          {showApproveButton && !showPublishButton && (
            <Button
              size="sm"
              onClick={() => onApprove(post.id)}
              disabled={isApproving}
              className="h-7 gap-1.5 text-xs"
            >
              <CheckCircle className="h-3.5 w-3.5" />
              {isApproving ? 'Approving...' : 'Approve'}
            </Button>
          )}
          {showEditButton && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(post)}
              className="h-7 gap-1.5 text-xs"
            >
              <Edit3 className="h-3.5 w-3.5" />
              Edit
            </Button>
          )}
          {showRegenerateButton && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onRegenerate(post)}
              className="h-7 gap-1.5 text-xs"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Regenerate
            </Button>
          )}
          {showDmButton && onDmTrigger && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDmTrigger(post)}
              className="h-7 gap-1.5 text-xs"
              title="DM people who comment a keyword"
            >
              <MessageSquareReply className="h-3.5 w-3.5" />
              {post.dm_keyword ? 'Comment DM' : 'Add comment DM'}
            </Button>
          )}
          {showDeleteButton && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDelete(post.id)}
              disabled={isDeleting}
              className="ml-auto h-7 gap-1.5 text-xs text-destructive hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
